import { useCallback } from 'react'
import type { RefObject } from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import type { TimeSuggestion, ParseResult } from '../types'
import {
  addMinutes,
  roundTimeToStep,
  clampTime,
} from '../utils/temporalHelpers'

interface UseKeyboardNavigationProps {
  isOpen: boolean
  suggestions: TimeSuggestion[]
  activeIndex: number
  matchIndex: number
  inputValue: string
  parseResult: ParseResult | null
  effectiveStep: number
  min: Temporal.PlainTime | undefined
  max: Temporal.PlainTime | undefined
  inputRef: RefObject<HTMLInputElement>
  isValidInput: (input: string) => boolean
  onOpenMenu: () => void
  onCloseMenu: () => void
  onNavigate: (direction: 'up' | 'down', length: number) => void
  onSetActiveIndex: (index: number, userInitiated: boolean) => void
  onSuggestionSelect: (suggestion: TimeSuggestion) => void
  onCommitValue: (time: Temporal.PlainTime) => void
}

export const useKeyboardNavigation = ({
  isOpen,
  suggestions,
  activeIndex,
  matchIndex,
  inputValue,
  parseResult,
  effectiveStep,
  min,
  max,
  inputRef,
  isValidInput,
  onOpenMenu,
  onCloseMenu,
  onNavigate,
  onSetActiveIndex,
  onSuggestionSelect,
  onCommitValue,
}: UseKeyboardNavigationProps) => {
  const handleMenuControl = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseMenu()
        inputRef.current?.focus({ preventScroll: true })
        return true
      }
      if (event.key === 'Tab' && isOpen) {
        onCloseMenu()
      }
      return false
    },
    [isOpen, inputRef, onCloseMenu],
  )

  const findNextEnabledIndex = useCallback(
    (startIndex: number, direction: 1 | -1): number => {
      if (suggestions.length === 0) return -1

      let nextIndex = startIndex
      let iterations = 0
      const maxIterations = suggestions.length

      do {
        nextIndex =
          (nextIndex + direction + suggestions.length) % suggestions.length
        iterations++

        if (iterations > maxIterations) {
          return startIndex
        }
      } while (suggestions[nextIndex]?.disabled)

      return nextIndex
    },
    [suggestions],
  )

  const handleNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (!isOpen || suggestions.length === 0) {
        return false
      }

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          const nextIndex = findNextEnabledIndex(activeIndex, 1)
          onSetActiveIndex(nextIndex, true)
          return true
        }
        case 'ArrowUp': {
          event.preventDefault()
          const nextIndex = findNextEnabledIndex(activeIndex, -1)
          onSetActiveIndex(nextIndex, true)
          return true
        }
        case 'Home': {
          event.preventDefault()
          let homeIndex = 0
          while (
            homeIndex < suggestions.length &&
            suggestions[homeIndex]?.disabled
          ) {
            homeIndex++
          }
          if (homeIndex < suggestions.length) {
            onSetActiveIndex(homeIndex, true)
          }
          return true
        }
        case 'End': {
          event.preventDefault()
          let endIndex = suggestions.length - 1
          while (endIndex >= 0 && suggestions[endIndex]?.disabled) {
            endIndex--
          }
          if (endIndex >= 0) {
            onSetActiveIndex(endIndex, true)
          }
          return true
        }
        default:
          return false
      }
    },
    [isOpen, suggestions, activeIndex, findNextEnabledIndex, onSetActiveIndex],
  )

  const handleTimeJumps = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (event.key !== 'PageUp' && event.key !== 'PageDown') {
        return false
      }

      if (!parseResult?.time) {
        return false
      }

      event.preventDefault()
      const delta = event.key === 'PageUp' ? -60 : 60
      let target = addMinutes(parseResult.time, delta)

      if (effectiveStep > 1) {
        target = roundTimeToStep(target, effectiveStep)
      }

      const clamped = clampTime(target, min, max)
      onCommitValue(clamped)
      return true
    },
    [effectiveStep, min, max, parseResult, onCommitValue],
  )

  const handleSelection = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (event.key !== 'Enter') {
        return false
      }

      event.preventDefault()

      if (isOpen && activeIndex >= 0 && activeIndex < suggestions.length) {
        const suggestion = suggestions[activeIndex]
        if (!suggestion.disabled) {
          onSuggestionSelect(suggestion)
          return true
        }
        // If the active suggestion is disabled, don't fall through to parsing logic
        return false
      }

      if (
        inputValue.trim() &&
        parseResult?.success &&
        parseResult.time &&
        isValidInput(inputValue)
      ) {
        const parsedTime = parseResult.time

        // Check if the parsed time matches any disabled suggestion
        const matchingDisabledSuggestion = suggestions.find(
          (s) =>
            s.disabled &&
            s.time.hour === parsedTime.hour &&
            s.time.minute === parsedTime.minute,
        )

        if (matchingDisabledSuggestion) {
          return false
        }

        onCommitValue(parsedTime)
        return true
      }

      return false
    },
    [
      activeIndex,
      inputValue,
      isOpen,
      parseResult,
      suggestions,
      onCommitValue,
      onSuggestionSelect,
      isValidInput,
    ],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === ' ') {
        event.stopPropagation()
      }

      if (handleMenuControl(event)) {
        return
      }

      if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        if (suggestions.length === 0) {
          return
        }
        event.preventDefault()
        // When opening with arrow keys and input is empty, scroll to matchIndex (current hour)
        // Otherwise select first item for ArrowDown, last for ArrowUp
        let targetIndex =
          inputValue.trim() === '' && matchIndex >= 0
            ? matchIndex
            : event.key === 'ArrowUp'
              ? suggestions.length - 1
              : 0

        // Skip disabled items
        if (suggestions[targetIndex]?.disabled) {
          const direction = event.key === 'ArrowUp' ? -1 : 1
          targetIndex = findNextEnabledIndex(targetIndex, direction)
        }

        onOpenMenu()
        onSetActiveIndex(targetIndex, true)
        return
      }

      if (handleNavigation(event)) {
        return
      }

      if (handleTimeJumps(event)) {
        return
      }

      if (handleSelection(event)) {
        return
      }

      if (event.key === 'ArrowDown' && !isOpen) {
        event.preventDefault()
        onOpenMenu()
      }
    },
    [
      handleMenuControl,
      handleNavigation,
      handleSelection,
      handleTimeJumps,
      isOpen,
      inputValue,
      matchIndex,
      suggestions,
      findNextEnabledIndex,
      onOpenMenu,
      onSetActiveIndex,
    ],
  )

  return { handleKeyDown }
}
